import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import * as sessionsApi from '../api/sessions.js'
import { useRPilotConfig } from '../composables/useRPilotConfig.js'

export const useSessionsStore = defineStore('sessions', () => {
  const cfg = useRPilotConfig()
  // Persist the active session id in sessionStorage (not localStorage) so a
  // host-driven iframe reload — e.g. swapping the iframe URL to toggle
  // sidebar/expanded mode — restores the in-progress conversation, while a
  // genuine fresh load (new tab, opening the dashboard) starts empty.
  // sessionStorage is scoped to the iframe's browsing context: it survives
  // src navigation but is gone the moment the tab closes or the iframe is
  // recreated from scratch.
  const storageKey = `${cfg?.namespace || 'rpilot'}.active-session-id`

  const sessions = ref([])
  const activeSessionId = ref(safeRead(storageKey))
  const activeSession = ref(null)
  const loading = ref(false)
  const error = ref(null)

  watch(activeSessionId, (id) => safeWrite(storageKey, id))

  const sortedSessions = computed(() =>
    [...sessions.value].sort((a, b) =>
      new Date(b.updatedAt) - new Date(a.updatedAt)
    )
  )

  async function load() {
    loading.value = true
    error.value = null
    try {
      sessions.value = await sessionsApi.listSessions()
    } catch (e) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  async function create(options = {}) {
    const session = await sessionsApi.createSession(options)
    activeSessionId.value = session.id
    activeSession.value = session
    upsert(session)
    return session
  }

  function setActive(id, session = null) {
    activeSessionId.value = id
    activeSession.value = session
  }

  /** Called when chat receives an implicit session id from the X-Session-Id header */
  function addImplicit(sessionData) {
    activeSessionId.value = sessionData.id
    activeSession.value = sessionData
    upsert(sessionData)
  }

  function upsert(session) {
    const idx = sessions.value.findIndex(s => s.id === session.id)
    if (idx === -1) sessions.value.push(session)
    else sessions.value[idx] = { ...sessions.value[idx], ...session }
  }

  /** Bump updatedAt locally after activity, so the list re-sorts without a refetch. */
  function touch(id) {
    const s = sessions.value.find(s => s.id === id)
    if (s) s.updatedAt = new Date().toISOString()
  }

  function clear() {
    activeSessionId.value = null
    activeSession.value = null
  }

  return {
    sessions, activeSessionId, activeSession, sortedSessions, loading, error,
    load, create, setActive, addImplicit, touch, clear,
  }
})

function safeRead(key) {
  try { return window.sessionStorage.getItem(key) || null }
  catch { return null }
}

function safeWrite(key, value) {
  try {
    if (value) window.sessionStorage.setItem(key, value)
    else window.sessionStorage.removeItem(key)
  } catch { /* sandboxed iframe — non-fatal */ }
}
